// slice-3690 — the "New Screens" slider (DEMO instance only).
//
// Chris, docs/demochanges.txt 2026-09-17:
//   "1. Under the user upper right corner - make a slider for 'New Screens' - default it on
//    remember it if turned off.
//    2. If it's turned on - and a v2 screen exists for the following screens then show it
//    instead of the 'old/current' screen that we have: work search, wave status, tag order
//    search, item search, adjust inventory search, location search and truck search."
//
// This file is the PURE half: the pref key, the seven faithful routes Chris named, and the
// pathname -> v2-route lookup. It lives in src/shared/ and therefore may not import the v2
// catalog itself (dep-cruiser `shared-not-from-features`), so the lookup takes the catalog as
// an argument; `src/app/newscreens/NewScreensSwap.tsx` binds the real one.

/**
 * usePrefState key. Per user, per browser, exactly like the criteria pane's Auto-hide tick
 * (dev-rules §4.25) -- default ON, remembered when turned OFF.
 */
export const NEW_SCREENS_PREF = 'newScreens';

/**
 * The seven screens Chris listed, as their PB-faithful routes. Scope is deliberately EXACT:
 * a child or param route of one of these (a drill target, a detail page) is not swapped,
 * because the v2 pages replace the SEARCH screen, not the tree hanging off it.
 *
 * Nothing else swaps -- the v2 catalog holds ~50 pages and this is a named, Chris-approved
 * subset, not "every v2 page we ever built".
 */
export const NEW_SCREENS_FAITHFUL_ROUTES: readonly string[] = [
  '/browse/work-search', // work search
  '/browse/wave-status', // wave status
  '/browse/order-tag-search', // tag order search
  '/browse/items-search', // item search
  '/browse/adjust-search', // adjust inventory search
  '/browse/cfg-locations', // location search
  '/browse/trucks', // truck search
];

/** The two fields of a V2CatalogEntry this module needs. Structurally satisfied by the real one. */
export type V2RouteEntry = {
  /** The hidden v2 route. */
  route: string;
  /** The untouched PB-faithful route ("PB view"). */
  faithfulRoute: string;
};

/** Trailing slashes only; anything else is compared verbatim so `/browse/trucksX` never matches. */
function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

/**
 * The v2 route that replaces {@code faithfulRoute}, or null when the catalog has no entry for
 * it yet. Catalog-driven on purpose: a v2 page that lands later with a matching
 * {@code faithfulRoute} joins the swap with no edit here.
 *
 * <p>Tie-break: three of the seven v2 pages are being built as `<faithful>2` (e.g.
 * `/browse/cfg-locations2`), and one faithful route -- `/browse/order-tag-search` -- can end up
 * with TWO catalog entries (the v2-campaign "Tag Order Search" at `/orders/waving2` and a newer
 * purpose-built one). A `<faithful>2` entry wins, because that is the page built for THIS
 * request; otherwise the first entry in catalog order wins. Deterministic either way -- never
 * "whichever slot file happened to sort first".
 */
export function v2RouteForFaithful(
  faithfulRoute: string,
  catalog: readonly V2RouteEntry[],
): string | null {
  const want = normalize(faithfulRoute);
  const matches = catalog.filter((e) => normalize(e.faithfulRoute) === want);
  const suffixed = matches.find((e) => normalize(e.route) === `${want}2`);
  const chosen = suffixed ?? matches[0];
  return chosen ? chosen.route : null;
}

/**
 * Where the browser should go instead of {@code pathname}, or null to stay put.
 *
 * <p>Returns null for every route that is not one of the seven, for the v2 routes themselves
 * (they are not faithful routes, so they can never redirect back -- no loop), and for a listed
 * route whose v2 page has not landed yet.
 */
export function newScreensTargetFor(
  pathname: string,
  catalog: readonly V2RouteEntry[],
  /** slice-4481: the tenant's chosen v2 routes; null/undefined = never saved → Chris's seven. */
  chosenV2Routes?: readonly string[] | null,
): string | null {
  const path = normalize(pathname);
  let target: string | null;
  if (chosenV2Routes == null) {
    if (!NEW_SCREENS_FAITHFUL_ROUTES.includes(path)) return null;
    target = v2RouteForFaithful(path, catalog);
  } else {
    // Only the v2 pages the tenant ticked can stand in for this route; the same tie-break
    // applies when two ticked pages replace the same faithful route.
    const chosen = new Set(chosenV2Routes.map(normalize));
    target = v2RouteForFaithful(
      path,
      catalog.filter((e) => chosen.has(normalize(e.route))),
    );
  }
  // A catalog entry that points a faithful route at ITSELF would loop forever. Cheap guard.
  if (!target || normalize(target) === path) return null;
  return target;
}

/**
 * slice-4481: the v2 routes the slider opens when the tenant never saved a list — the v2 page
 * for each of Chris's seven (what the swap did before the list became configurable).
 */
export function defaultNewScreensV2Routes(catalog: readonly V2RouteEntry[]): string[] {
  return NEW_SCREENS_FAITHFUL_ROUTES.map((r) => v2RouteForFaithful(r, catalog)).filter(
    (r): r is string => !!r,
  );
}
